<script>
import moment from 'moment'
import { mapGetters } from 'vuex'
import CalendarResource from '@/resources/calendar'
import AvailabilityResource from '@/resources/availability'
import PackageDealResource from '@/resources/packageDeal'
import Checkout from '../components/Checkout.vue'
import WaiverModal from '../components/WaiverModal.vue'
import ReservationRacersForm from '../components/ReservationRacersForm.vue'
import DateStepper from '../components/DateStepper.vue'
import { TIMEZONE } from '@/config'
import { timeDateFormattedPretty, dateFormattedPretty } from '@/util'

export default {
    components: {
        Checkout,
        WaiverModal,
        ReservationRacersForm,
        DateStepper,
    },
    computed: {
        ...mapGetters(['userResource', 'locationResources', 'locationNearest']),
        locationCurrencySymbol() {
            return this.selectedLocation?.attributes?.currency_symbol || '$'
        },
        selectedDateFormatted() {
            return moment(this.selectedDate).format('YYYY-MM-DD')
        },
        sessionsMinutes() {
            return this.sessions * 15
        },
        dealMode() {
            return !!this.dealResource
        },
        multiSimBooking() {
            return !!(this.lastCommitData && this.lastCommitData.reservation_ids && this.lastCommitData.reservation_ids.length > 1)
        },
        dealPriceLabel() {
            if (!this.dealResource) {
                return ''
            }
            const attrs = this.dealResource.attributes
            const symbol = attrs.readonly_values?.currency_symbol || this.locationCurrencySymbol
            if (attrs.pricing_mode === 'FLAT') {
                return `${symbol}${this.moneyFormat(attrs.price_cents)} total per racer`
            }
            if (attrs.adjustment_type === 'PERCENTAGE') {
                return `${attrs.value}% ${attrs.direction === 'SURCHARGE' ? 'surcharge' : 'off'}`
            }
            return `${symbol}${this.moneyFormat(attrs.value)} ${attrs.direction === 'SURCHARGE' ? 'surcharge' : 'off'}`
        },
        pricingRuleDiscounts() {
            const rules = {}
            for (const reserv of this.reservs) {
                const rule = reserv.attributes.pricing_rule
                if (rule && rule.direction === 'DISCOUNT') {
                    rules[rule.id] = rule
                }
            }
            return Object.values(rules)
        },
        dealRacersFixed() {
            return this.dealMode && !!this.dealResource.attributes.racers
        },
        dealStartMinutes() {
            const t = this.dealResource?.attributes?.start_time
            return t ? this.hhmmToMinutes(t) : null
        },
        dealLatestStartMinutes() {
            // Backend-computed: end_time minus (sessions x per-session length), so the whole run fits
            const t = this.dealResource?.attributes?.readonly_values?.latest_start_time
            return t ? this.hhmmToMinutes(t) : null
        },
        dealHasTimeWindow() {
            return this.dealMode && (this.dealStartMinutes !== null || this.dealLatestStartMinutes !== null)
        },
        dealValidDays() {
            return this.dealResource?.attributes?.readonly_values?.valid_days || []
        },
        calendars() {
            let ids = []
            return this.calendarData.filter((calendar) => {
                if (!ids.includes(calendar.id)) {
                    ids.push(calendar.id)
                    return true
                }

                return false
            })
        },
        calendarWrapperClasses() {
            if (this.calendarForceVisible) {
                return 'd-block d-sm-none d-lg-block'
            } else {
                return 'd-none d-lg-block'
            }
        },
    },
    watch: {
        selectedDate() {
            this.selectedSlotData = null
            this.load()
        },
        selectedLocation() {
            if (!this.loading) {
                this.selectedSlotData = null
                this.load()
                const newRoute = { ...this.$route }
                newRoute.query = { location: this.selectedLocation.id }
                this.$router.replace(newRoute)
            }
        },
        selectedSlotData() {
            if (!this.loading) {
                this.load()
            }
        },
        sessions() {
            this.load()
        },
        racers() {
            this.load()
        },
    },
    data() {
        const now = new Date()
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        const maxDate = new Date(now.getFullYear(), now.getMonth() + 2, now.getDate())
        return {
            now,
            today,
            maxDate,
            minDate: today,
            loading: true,
            selectedDate: today,
            avails: [],
            reservs: [],
            calendarData: [],
            calendarForceVisible: false,
            selectedLocation: null,
            selectedSlotData: null,
            bookModalSize: 'lg',
            bookingErrorDetails: '',
            pendingCommitData: null,
            lastCommitData: null,
            completing: false,
            sessions: 1,
            racers: 1,
            dealResource: null,
        }
    },
    methods: {
        dateFormattedPretty,
        timeDateFormattedPretty,
        async load() {
            this.loading = true
            this.calendarData = []
            await this.userResource.update()

            if (this.$route.query.location) {
                this.selectedLocation = this.locationResources.find((location) => location.id == this.$route.query.location)
            } else if (!this.selectedLocation && this.userResource.attributes.location) {
                this.selectedLocation = this.locationResources.find((location) => location.id == this.userResource.attributes.location)
            } else if (!this.selectedLocation) {
                if (this.locationNearest) {
                    this.selectedLocation = this.locationResources.find((location) => location.id == this.locationNearest.id)
                } else {
                    this.selectedLocation = this.locationResources[0]
                }
            }

            this.avails = await AvailabilityResource.quote(this.selectedDateFormatted, this.selectedLocation.id)

            if (!this.selectedSlotData) {
                this.selectedSlotData = this.findNearestSlot()
            }

            if (!this.selectedSlotData) {
                // No availability  -- force null just in case it's false or 0 etc
                this.selectedSlotData = null
                this.calendarForceVisible = true

                this.$nextTick(() => {
                    this.addMessage('danger', 'No availability for the selected date')
                    this.loading = false
                })

                return false
            }

            // @todo Just noticed these are accidentally swapped -- reservs should be cals and vice versa
            this.reservs = await CalendarResource.parallel(this.selectedDateFormatted, this.selectedSlotData.block, this.selectedLocation.id, { sessions: this.sessions, racers: this.racers, deal: this.dealResource ? this.dealResource.id : null })

            const calendarAttrs = this.reservs.map(async (reserv) => {
                return {
                    id: await reserv.resolveAttribute('calendar.id'),
                    name: await reserv.resolveAttribute('calendar.name'),
                    costCents: await reserv.resolveAttribute('price_cents'),
                    costUnit: await reserv.resolveAttribute('calendar.cost_unit'),
                    blockLength: await reserv.resolveAttribute('calendar.reservation_block_length'),
                    type: await reserv.resolveAttribute('calendar.type.name'),
                    image: await reserv.resolveAttribute('calendar.image.url'),
                    imageBackup: await reserv.resolveAttribute('calendar.type.image.url'),
                    typeDescription: await reserv.resolveAttribute('calendar.type.description'),
                }
            })

            const calendarsAll = await Promise.all(calendarAttrs)
            this.calendarData = calendarsAll.flat(1)
            this.loading = false
        },
        pricingRuleAmount(rule) {
            return rule.adjustment_type === 'PERCENTAGE' ? `${rule.value}%` : `${this.locationCurrencySymbol}${this.moneyFormat(rule.value)}`
        },
        hhmmToMinutes(hhmm) {
            const [h, m] = hhmm.split(':').map(Number)
            return h * 60 + m
        },
        startFitsDealWindow(isoTime) {
            const d = new Date(isoTime)
            const startMin = d.getHours() * 60 + d.getMinutes()
            if (this.dealStartMinutes !== null && startMin < this.dealStartMinutes) {
                return false
            }
            // latest start = deal end minus the full session run, so every session finishes in-window
            if (this.dealLatestStartMinutes !== null && startMin > this.dealLatestStartMinutes) {
                return false
            }
            return true
        },
        availAllowed(avail) {
            if (new Date(avail.time) < this.now) {
                return false
            }
            if (this.dealMode && !this.startFitsDealWindow(avail.time)) {
                return false
            }
            return true
        },
        dateDisabledForDeal(ymd, date) {
            if (!this.dealMode || !this.dealValidDays.length) {
                return false
            }
            const isoDay = date.getDay() === 0 ? 7 : date.getDay()
            return !this.dealValidDays.includes(isoDay)
        },
        findNearestSlot() {
            for (const key of Object.keys(this.avails)) {
                if (this.availAllowed(this.avails[key])) {
                    return this.avails[key]
                }
            }

            return false
        },
        goAdvanced() {
            const target = { name: 'reservationsAdvanced', query: { location: this.selectedLocation ? this.selectedLocation.id : '' } }
            if (!this.dealMode) {
                this.$router.push(target)
                return
            }
            this.$bvModal
                .msgBoxConfirm(`Switching to pick-and-choose will remove the "${this.dealResource.attributes.name}" deal and its pricing. Continue?`, {
                    title: 'Leave this deal?',
                    okTitle: 'Continue without deal',
                    cancelTitle: 'Stay on this deal',
                    okVariant: 'danger',
                    centered: true,
                })
                .then((confirmed) => {
                    if (confirmed) {
                        this.$router.push(target)
                    }
                })
        },
        forceCalendarOpen() {
            this.calendarForceVisible = true
            window.scroll(0, 0)
        },
        openReservationsModal() {
            this.$refs.bookModal.show()
        },
        closeReservationsModal() {
            this.$refs.bookModal.hide()
        },
        onBookingCreate(commitData) {
            this.closeReservationsModal()
            this.$analytics.track('created_reservation')
            this.lastCommitData = commitData

            if (this.$refs.waiverModal.hasOutstanding()) {
                this.pendingCommitData = commitData
                this.$refs.waiverModal.next()
            } else if (commitData.redirect) {
                window.location.href = commitData.redirect
            } else if (this.selectedLocation?.attributes.post_booking_url) {
                window.location.href = this.selectedLocation.attributes.post_booking_url
            } else {
                this.$refs.completeModal.show()
            }
        },
        onBookingError(message) {
            this.closeReservationsModal()
            this.bookingErrorDetails = message
            this.$refs.errorModal.show()
        },
        onBookAnother() {
            window.location.reload()
        },
        async completeBooking() {
            this.completing = true
            if (this.$refs.racersForm) {
                const ok = await this.$refs.racersForm.run()
                if (!ok) {
                    this.completing = false
                    return
                }
            }
            this.onBookAnother()
        },
    },
    async mounted() {
        if (this.$route.query.deal) {
            try {
                this.dealResource = await PackageDealResource.detail(this.$route.query.deal)
                this.sessions = this.dealResource.attributes.sessions || 1
                if (this.dealResource.attributes.racers) {
                    this.racers = this.dealResource.attributes.racers
                }
                const dealLocation = this.locationResources.find((location) => location.id == this.dealResource.attributes.location)
                if (dealLocation) {
                    this.selectedLocation = dealLocation
                }
                const { valid_from, valid_until } = this.dealResource.attributes
                if (valid_from) {
                    const from = new Date(`${valid_from}T00:00:00`)
                    if (from > this.minDate) {
                        this.minDate = from
                    }
                }
                if (valid_until) {
                    this.maxDate = new Date(`${valid_until}T00:00:00`)
                }
                if (this.selectedDate < this.minDate || this.selectedDate > this.maxDate) {
                    this.selectedDate = this.minDate
                }
            } catch (e) {
                this.addMessage('warning', 'That deal could not be loaded -- showing standard booking.')
            }
        }
        await this.load()
    },
}
</script>

<template>
    <div>
        <header-image kicker="Réserver un simulateur">
            <h1>Réservations</h1>
        </header-image>

        <b-container class="mt-3">
            <messages />

            <b-alert v-if="!calendarForceVisible && selectedSlotData" show variant="info" class="d-block d-sm-none"> <strong>Date sélectionnée :</strong> {{ dateFormattedPretty(selectedSlotData.time) }} (<a href="javascript:{}" @click="forceCalendarOpen()">modifier</a>)<br /> </b-alert>

            <b-row>
                <b-col md="4" :class="calendarWrapperClasses">
                    <b-card bg-variant="light" class="mb-3">
                        <date-stepper class="d-block" :value="selectedDate" :min="minDate" :max="maxDate" :disabled-fn="dateDisabledForDeal" @input="(v) => (selectedDate = v)" />
                    </b-card>

                    <b-button variant="primary" class="d-none d-sm-block" :disabled="!reservs.length" @click="openReservationsModal()" block>Passer au paiement</b-button>
                </b-col>

                <b-col>
                    <b-alert variant="primary" show v-if="dealMode">
                        <b-icon icon="tag-fill" /> Réservation avec une offre : <strong>{{ dealResource.attributes.name }}</strong> &mdash; {{ dealPriceLabel }} &middot; {{ sessions === 1 ? '1 séance' : `${sessions} séances` }} par pilote.
                        <span class="d-block"
                            ><small>{{ dealRacersFixed ? `Cette offre est pour ${racers} pilote(s).` : "Le nombre de séances est défini par l’offre — choisissez le nombre de pilotes ci-dessous." }}</small></span
                        >
                    </b-alert>

                    <b-card header="Lieu" class="mb-3" v-if="locationResources.length > 1">
                        <b-form-select v-model="selectedLocation" :disabled="dealMode">
                            <b-form-select-option v-for="(location, i) in locationResources" :key="i" :value="location">{{ location }}</b-form-select-option>
                        </b-form-select>
                    </b-card>

                    <b-card header="Heure de la course" class="mb-3">
                        <b-form-select v-model="selectedSlotData">
                            <b-form-select-option v-for="(avail, i) in avails" :key="i" :value="avail" :disabled="!availAllowed(avail)">{{ timeDateFormattedPretty(avail.time) }}</b-form-select-option>
                        </b-form-select>
                        <small class="text-muted d-block" v-if="dealHasTimeWindow"> Les heures de début sont limitées afin que les {{ sessions }} séance(s) se terminent pendant la période de validité de l’offre. </small>
                    </b-card>
                    <b-row>
                        <b-col>
                            <b-card :header="dealRacersFixed ? 'Nombre de pilotes (défini par l’offre)' : 'Nombre de pilotes'" class="mb-3">
                                <b-form-spinbutton v-model="racers" :disabled="dealRacersFixed" />
                            </b-card>
                        </b-col>
                        <b-col>
                            <b-card :header="dealMode ? 'Séances de course (définies par l’offre)' : 'Séances de course'" class="mb-3">
                                <b-form-spinbutton v-model="sessions" :disabled="dealMode" />
                            </b-card>
                        </b-col>
                    </b-row>

                    <b-alert variant="primary" show v-for="rule in (loading ? [] : pricingRuleDiscounts)" :key="rule.id">
                        <b-icon icon="tag-fill" /> Remise appliquée : <strong>{{ rule.name }}</strong> &mdash; {{ pricingRuleAmount(rule) }} de réduction par séance<template v-if="rule.min_simulators > 1 || rule.min_consecutive_sessions > 1"> en réservant <template v-if="rule.min_simulators > 1">{{ rule.min_simulators }} simulateurs ou plus</template><template v-if="rule.min_simulators > 1 && rule.min_consecutive_sessions > 1">, chacun pour </template><template v-if="rule.min_consecutive_sessions > 1">{{ rule.min_consecutive_sessions }} séances consécutives ou plus</template></template>.
                    </b-alert>
                    <div v-if="selectedSlotData && !loading" class="mb-3">
                        <b-alert variant="success" :show="reservs.length > 0"
                            ><b-icon icon="check-circle-fill" /> <strong>Excellent !</strong> Nous avons {{ racers === 1 ? '1 simulateur' : `${racers} simulateurs` }} disponible(s) pour <strong>{{ timeDateFormattedPretty(selectedSlotData.time) }}</strong></b-alert
                        >
                        <b-alert variant="danger" :show="!reservs.length && racers > 1"
                            ><b-icon icon="exclamation-triangle" /> <strong>Désolé !</strong> Nous n’avons pas {{ racers === 1 ? '1 simulateur' : `${racers} simulateurs` }} disponible(s) pour <strong>{{ timeDateFormattedPretty(selectedSlotData.time) }}</strong></b-alert
                        >
                        <b-alert variant="danger" :show="!reservs.length && racers == 1"
                            ><b-icon icon="exclamation-triangle" /> <strong>Désolé !</strong> Aucun simulateur n’est disponible pour <strong>{{ timeDateFormattedPretty(selectedSlotData.time) }}</strong></b-alert
                        >
                    </div>

                    <div v-else-if="loading" class="mb-3 text-center">
                        <b-spinner type="grow" label="Chargement..." class="mt-3" /><br />
                        <small>Recherche de simulateurs disponibles...</small>
                    </div>

                    <b-list-group class="mb-3">
                        <b-list-group-item v-if="calendars.length">
                            <small class="mr-auto">Simulateur</small>
                            <small class="float-right">Type et coût par séance</small>
                        </b-list-group-item>
                        <b-list-group-item v-for="(calendar, i) in calendars" :key="i" class="d-flex align-items-center">
                            <b-avatar :src="calendar.image || calendar.imageBackup" variant="info" class="mr-3 flex-shrink-0" rounded />
                            <div class="d-flex flex-column flex-lg-row align-items-start align-items-lg-center flex-grow-1 mr-2">
                                <span class="mr-lg-auto">{{ calendar.name }}</span>
                                <small class="mr-lg-3"
                                    ><a href="javascript:{}" v-b-modal="`infoModal-${calendar.id}`">{{ calendar.type }} <b-icon icon="question-circle" /></a
                                ></small>
                            </div>
                            <div class="d-flex flex-column-reverse flex-lg-row align-items-end align-items-lg-center flex-shrink-0 text-right">
                                <small v-if="dealMode && dealResource.attributes.pricing_mode === 'FLAT'">Total pour {{ sessions * calendar.blockLength * 15 }} minutes</small>
                                <small v-else>{{ calendar.costUnit }}</small>
                                <b-badge variant="success" class="ml-lg-3">{{ locationCurrencySymbol }}{{ moneyFormat(calendar.costCents) }}</b-badge>
                            </div>

                            <b-modal :id="`infoModal-${calendar.id}`" :title="`Simulateur ${calendar.type}`" ok-only no-fade centered>
                                <b-embed v-if="calendar.type && calendar.type.match(/haptic/gi)" type="iframe" aspect="16by9" src="https://www.youtube.com/embed/S-pldT_FWck" allowfullscreen />
                                <p class="my-4">{{ calendar.typeDescription }}</p>
                            </b-modal>
                        </b-list-group-item>
                    </b-list-group>

                    <b-alert variant="light" show><b-icon icon="question-circle" /> Ceci est la vue simplifiée. Vous pouvez également <a href="javascript:{}" @click="goAdvanced">choisir les simulateurs que vous souhaitez</a>.</b-alert>
                </b-col>
            </b-row>

            <div class="d-block d-sm-none pt-5 pb-5" />
        </b-container>

        <b-container class="mt-3 d-block d-sm-none pt-3 pb-3" id="taco" fluid>
            <b-row>
                <b-col class="text-center text-muted">
                    <b-button variant="primary" @click="openReservationsModal()" :disabled="!reservs.length" class="col-sm-12">Passer au paiement</b-button>
                    <small v-if="reservs.length">{{ reservs.length === 1 ? '1 créneau sélectionné' : `${reservs.length} créneaux sélectionnés` }} avec {{ calendars.length === 1 ? '1 simulateur' : `${calendars.length} simulateurs` }}</small>
                    <small v-else>Aucune disponibilité trouvée. Relancez votre recherche.</small>
                </b-col>
            </b-row>
        </b-container>

        <!-- Modals -->
        <b-modal ref="bookModal" :size="bookModalSize" title="Paiement" hide-footer no-fade centered>
            <checkout :reservation-resources="reservs" :package-deal="dealResource" @size="(newSize) => (bookModalSize = newSize)" @success="(commitData) => onBookingCreate(commitData)" @error="onBookingError"> </checkout>
        </b-modal>

        <b-modal ref="completeModal" title="Terminé !" size="md" hide-footer no-fade no-close-on-backdrop no-close-on-esc hide-header-close no-enforce-focus centered scrollable>
            <h2 class="h5 mb-3">{{ multiSimBooking ? 'Réservation confirmée' : 'Simulateur réservé avec succès' }}</h2>
            <reservation-racers-form v-if="multiSimBooking" ref="racersForm" :reservation-ids="lastCommitData.reservation_ids" :purchaser="userResource" />
            <p v-else class="mb-0 text-muted">Votre simulateur est réservé.</p>

            <div class="d-flex justify-content-between align-items-center mt-4">
                <b-button variant="link" class="px-0" @click="$router.push({ name: 'logout' })">Se déconnecter</b-button>
                <b-button variant="secondary" :disabled="completing" @click="completeBooking">
                    <b-spinner v-if="completing" small />
                    {{ multiSimBooking ? 'Envoyer les invitations &amp; réserver à nouveau' : 'Réserver à nouveau' }}
                </b-button>
            </div>
        </b-modal>

        <b-modal ref="errorModal" title="Erreur de réservation" ok-title="Vérifier le mode de paiement" cancel-title="Retour" @cancel="onBookAnother" @ok="$router.push({ name: 'dashboard' })" cancel-variant="primary" no-fade no-close-on-backdrop no-close-on-esc hide-header-close centered>
            <h3>Une erreur est survenue lors de la réservation</h3>
            <b-alert variant="danger" show
                >Votre réservation n’a pas pu être finalisée en raison de problèmes lors du traitement de votre carte.<br />Détails de l’erreur : <strong>{{ bookingErrorDetails }}</strong></b-alert
            >
        </b-modal>

        <b-modal ref="apexClubModal" size="md" :title="$appConfig('APEX_CLUB_NAME', 'Apex Club')" hide-footer no-fade centered>
            <apex-club-pitch @success="$refs.apexClubModal.hide()" />
        </b-modal>

        <waiver-modal ref="waiverModal" @success="onBookingCreate(pendingCommitData)" />
    </div>
</template>

<style lang="scss">
#taco {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    z-index: 999;
    background-color: var(--panel);
    color: var(--ink);
    border-top: 1px solid var(--line-2);
    box-shadow: 0 -10px 30px rgba(0, 0, 0, 0.28);
}
:root[data-theme='dark'] #taco {
    background-color: var(--panel-2);
    border-top-color: var(--line-2);
    box-shadow: 0 -10px 30px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.05);
}
</style>
